'use client'

import React, { useMemo } from 'react'
import { Box } from '@mantine/core'
import CarouselV2 from '@/components/Refresh/CategoryPages/CarouselV2'
import { PageLocation } from '@nile/mantine-theme/analytics'
import { RenderedServicesType, ServiceItemCardType } from '@/types/enums/ServiceItem'
import { useRenderedServices } from '@/hooks/useRenderedServices'
import { enrichRenderedServicePricing } from '@/components/Store/StoreUtilities'
import type { RenderedService } from '@/types/interfaces/service-item'
import type { SkuRelatedServices } from '@/types/interfaces/sku'

export type SkuRelatedProductsProps = {
    skuId: string
    skuName: string
    authenticated: boolean
    relatedServicesData: SkuRelatedServices
}

export function SkuRelatedProducts({ skuId, skuName, authenticated, relatedServicesData }: SkuRelatedProductsProps) {
    const enriched = useMemo(() => {
        const rs = structuredClone(relatedServicesData)
        enrichRenderedServicePricing(rs as unknown as RenderedService, authenticated)
        rs.services = rs.services.filter((s) => String(s.skuId) !== String(skuId))
        return rs
    }, [relatedServicesData, authenticated, skuId])

    if (!enriched.services.length) return null

    const { id, title, subtitle, button, type, cardType, services } = enriched
    return (
        <Box id="skuRelatedProducts" w="100%" miw={0} mt={60} mb={48}>
            <CarouselV2
                categoryName={id}
                service={{
                    id,
                    services,
                    title,
                    subtitle: subtitle ?? undefined,
                    button,
                    type,
                    cardType,
                }}
                pageLocation={PageLocation.DETAIL_PAGE}
                additionalInformation={{ sku_id: skuId, sku_name: skuName }}
                disableWholeBodyLayout
            />
        </Box>
    )
}

export type SkuRelatedProductsByCategoryProps = {
    skuId: string
    skuName: string
    category: 'base' | 'weight-loss' | 'labs' | 'metabolic-health'
}

export function SkuRelatedProductsByCategory({ category, skuId, skuName }: SkuRelatedProductsByCategoryProps) {
    const { services, relatedServices, loading } = useRenderedServices(category)

    const sourceServices = category === 'base' ? services : relatedServices
    if (loading || sourceServices.length === 0) return null

    const group = sourceServices.find((sg) => sg?.services?.length)
    if (!group) return null

    const filteredServices = group.services.filter((service) => service.skuId !== skuId)
    if (filteredServices.length === 0) return null

    return (
        <Box id="skuRelatedProducts" w="100%" miw={0} mt={60} mb={48}>
            <CarouselV2
                categoryName={category}
                service={{
                    id: group.id,
                    services: filteredServices,
                    title: group.title,
                    subtitle: group.subtitle,
                    button: group.button,
                    type: RenderedServicesType.carousel,
                    cardType: group.cardType ?? ServiceItemCardType.productCard,
                }}
                pageLocation={PageLocation.DETAIL_PAGE}
                additionalInformation={{ sku_id: skuId, sku_name: skuName }}
                disableWholeBodyLayout
            />
        </Box>
    )
}
